import { styled, u, keyframes } from '../../shared/theming-sc'
import { keyframesFadeIn } from '../../shared/utilities/styleUtils'

const cellHeightPx = '60px'
const primaryColor = u.getColor('greyDark')
const secondaryColor = u.getColor('greyLight')
const borderColor = u.getColor('greyMuted')
const radius = '5px'
const mediumGap = u.getSpacing('small') + 'px'
const defaultWidth = '100%'
const listItemCommon = `
  outline: 0;
  margin: 0 !important;
  padding-top: 18px;
  padding-bottom: 18px;
  line-height: ${mediumGap};
  padding-right: 35px;
  max-width: 100%;
  height: ${cellHeightPx};
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledOuter = styled.div.attrs({
  style: ({useBreakout}) => ({ 
    paddingBottom: useBreakout ? cellHeightPx : 0
  })
})`
  position: relative;
  margin-bottom: 20px;
`

const StyledWrapper = styled.div.attrs({
  style: ({useBreakout}) => ({ 
    position: useBreakout ? 'absolute' : 'relative',
  })
})`
  position: absolute;
  width: 100%;
`

// The DROPDOWN, a.k.a. this.dropdownEl
const StyledDropdownContainer = styled.div.attrs({
  style: ({visible}) => {
    return {
      transition: `height ${visible ? '0.35s' : '0.2s'} cubic-bezier(${visible ? '0.35,0.75,0,1' : '0.0035,0.85,0,1'}), box-shadow 0.2s ease-in-out, transform ${visible ? '0.35s' : '0.2s'} cubic-bezier(${visible ? '0.35,0.75,0,1' : '0.0035,0.85,0,1'})`
    }
  }
})`
  box-shadow: 1px 1px 1px rgba(0,0,0,0.0);
  border-radius: ${radius};
  width: ${props => props.multiheaderEnabled ? '100%' : ''};
  min-width: ${props => props.multiheaderEnabled ? '280px' : ''};
  height: ${cellHeightPx};
  background: #fff;
  overflow: hidden;
  margin-bottom: 15px;
  border: 1px solid ${borderColor};
`

const StyledButtonDropdown = styled.div`
  display: table;
  position: relative;
  width: 100%;
  height: ${cellHeightPx};
  color: ${props => props.enabled ? primaryColor : secondaryColor};
  overflow: hidden;
  outline: 0;
  cursor: default;
  border-bottom: solid 1px ${borderColor};
  border-spacing: 0;
  border-collapse: separate;
  border-top-right-radius: ${radius};
  border-top-left-radius: ${radius};
  border-bottom-left-radius: ${props => !props.listVisible ? radius : ''};
  border-bottom-right-radius: ${props => !props.listVisible ? radius : ''};
  background-color: white;
`

const StyledDropdownSpan = styled.span`
  box-sizing: border-box;
`

const StyledDropdownCaretArea = styled.span`
  cursor: pointer;
  display: table-cell;
  position: absolute;
  text-align: center;
  vertical-align: middle;
  width: 25px;
  right: 18px;
  top: 19px;
  box-sizing: border-box;
`

const StyledDropdownCaret = styled.span`
  display: inline-block;
  height: 15px;
  width: 20px;
  position: relative;
  box-sizing: border-box;
`

const StyledOptionsOuter = styled.div`
  width: ${defaultWidth};
  margin-top: -1px;
  top: 100%;
  background: white;
  transform-origin: 50% 0;
  animation: ${keyframesFadeIn} 0.5s ease-out;
`

const StyledOptionsList = styled.ul`
  border-collapse: collapse;
  width: ${defaultWidth};
  max-height: 420px;
  overflow: scroll;
  overflow-x: hidden;
  -webkit-padding-start: 0;
  margin: 0 !important;
  padding-left: 0;
`

// @TODO This should probably be two separate items, instead of using props excessively
const StyledOption = styled.li`
  ${listItemCommon};
  border-top: 1px solid ${borderColor};
  padding-left: ${u.getSpacing('small')}px;
  border-bottom-right-radius: ${props => props.isLastChild ? radius : ''};
  border-bottom-left-radius: ${props => props.isLastChild ? radius : ''};
  border-bottom: ${props => props.isLastChild ? '1px solid ' + borderColor : ''};
  text-align: left;
  color: ${props => props.alreadySelected ? secondaryColor
    : props.headerType === 'dropdownLabel' ? secondaryColor
      : primaryColor};
  font-size: ${props => props.headerType === 'dropdownHeader' ? u.getFontSize('tn')
    : props => props.headerType === 'dropdownOptions' ? u.getFontSize('md')
      : ''}px;
  font-family: ${props => props.headerType === 'dropdownHeader' ? u.getFontFamily('knowledge', 'black')
    : props => props.headerType === 'dropdownOptions' ? u.getFontFamily('knowledge', 'regular')
      : ''};
  letter-spacing: ${props => props.headerType === 'dropdownHeader' ? '2px'
    : props => props.headerType === 'dropdownOptions' ? 'normal'
      : ''};
  font-stretch: ${props => props.headerType === 'dropdownHeader' ? 'normal' : ''};

  border-left: 3px solid transparent;
  transition: border-color 0.2s linear, padding-left 0.175s ease;
  cursor: default;

  &:hover {
    border-left: 3px solid ${props => props.headerType === 'dropdownHeader' ? 'transparent' : u.getColor('primary')};
    padding-left: ${props => props.headerType === 'dropdownHeader' ? u.getSpacing('small') + 'px' : '23px'}
  }

  .user__input-highlight {
    font-weight: 500;
  }
  
  & * {
    pointer-events: none;
  }
`

const StyledDropdownValue = styled.div.attrs({
  style: ({ fade }) => ({
    animation: fade ? `${keyframesFadeIn} 0.35s ease-in-out` : ''
  })
})`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  padding: 17px 35px 0 19px;
  line-height: 24px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledDropdownLabel = styled.label`
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  color: ${props => props.isSearchPlaceholder ? secondaryColor : primaryColor};
  font-family: ${props => u.getFontFamily('knowledge', (props.isSearchPlaceholder || props.isDefaultLabel) ? 'regular' : 'medium')},
  font-size: ${u.getFontSize('md')};
  font-weight: ${props => (props.isSearchPlaceholder || props.isDefaultLabel) ? 400 : 500};
`

const StyledTextInput = styled.input.attrs({
  type: 'text',
  autoComplete: 'off',
  style: ({listVisible}) => ({
    // This way it'll sit on top of the placeholder when open, won't close when clicking to focus
    position: listVisible ? 'relative' : 'static'
  })
})`
    height: ${cellHeightPx};
    padding-left: 20px;
    margin: 0 !important;
    border: 0 !important; // override dataset styles :(
    outline: none;
    font-size: 100%;
    background: transparent;

    &::-ms-clear {
      display: none;
      height: 0;
      width: 0;
    }

    &::-ms-reveal {
      display: none; 
      width : 0; 
      height: 0; 
    }
  `

const keyframesTagIn = keyframes`
  from{
    height: 0;
    padding: 0 10px 0 15px;
    margin: 0;
    transform: scale(0.85,0.85);
  }
  to{
    height: 40px;
    padding: 11px 10px 9px 15px;
    margin: 0 0 10px 0;
    transform: scale(1,1);
  }
`
const StyledMultiSelectTag = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85em;
  border-radius: 3px;
  border: solid 1px ${borderColor};
  background-color: ${u.getColor('greyLightest')};
  transform-origin: 50% 80px;
  animation: ${keyframesTagIn} 0.4s cubic-bezier(0.3,0.6,0,1), ${keyframesFadeIn} 0.65s ease-in-out;
  animation-fill-mode: both;
`

const StyledPrimaryLabel = styled.label`
  display: block;
  padding: 0 0 9px 2px;
  color: ${primaryColor};
  font-weight: 400;
`

const StyledSecondaryLabel = styled.label`
  display: block;
  padding: 0 0 7px 2px;
  font-size: 0.85em;
  color: ${secondaryColor};
`

const StyledClearButton = styled.button`
  display: inline-block;
  padding: 0;
  line-height: 1;
  border: 0;
  background-color: transparent;
  cursor: pointer
`

const StyledNoResultsMessage = styled.div`
  ${listItemCommon};
  padding-left: ${u.getSpacing('small')}px;
  border: 1px solid ${borderColor};
  border-top-width: 0;
  border-right-width: 0;
  border-bottom-left-radius: ${radius};
  border-bottom-right-radius: ${radius};
`

const StyledOptionDivWrapper = styled.div``

export {
  StyledOuter,
  StyledWrapper,
  StyledDropdownContainer,
  StyledButtonDropdown,
  StyledDropdownSpan,
  StyledDropdownCaretArea,
  StyledDropdownCaret,
  StyledOptionsOuter,
  StyledOptionsList,
  StyledOption,
  StyledDropdownValue,
  StyledDropdownLabel,
  StyledTextInput,
  StyledNoResultsMessage,
  StyledMultiSelectTag,
  StyledPrimaryLabel,
  StyledSecondaryLabel,
  StyledClearButton,
  StyledOptionDivWrapper
}
